/**
 * BigInt-safe JSON codec for the transaction journal.
 *
 * `JSON.stringify` THROWS on BigInt ("Do not know how to serialize a BigInt"),
 * and essentially every argument we journal is one — `approve(spender, amount)`,
 * `handleTokenBurn(...)`, gas limits, `value`. Without this codec the very first
 * journal write of a real flow would throw, and because the journal is also read
 * at app boot, a half-written entry would take the whole app down.
 *
 * Encoding is a tagged wrapper rather than a bare string so that round-tripping
 * is lossless: a genuine string "123" stays a string, while 123n becomes a
 * BigInt again. `Uint8Array` gets the same treatment because viem hands back
 * byte arrays for some ABI types and a plain JSON round-trip would silently
 * turn them into `{"0": 12, "1": 34}` objects.
 */

const BIGINT_TAG = "__frdm_bigint";
const BYTES_TAG = "__frdm_bytes";

function replacer(key, value) {
  if (typeof value === "bigint") {
    return { [BIGINT_TAG]: value.toString() };
  }
  // Uint8Array survives as a tagged hex string. Checked before the generic
  // object branch because it is an object.
  if (value instanceof Uint8Array) {
    let hex = "";
    for (let i = 0; i < value.length; i += 1) {
      hex += value[i].toString(16).padStart(2, "0");
    }
    return { [BYTES_TAG]: hex };
  }
  return value;
}

function reviver(key, value) {
  if (value && typeof value === "object") {
    if (typeof value[BIGINT_TAG] === "string") {
      // eslint-disable-next-line no-undef
      return BigInt(value[BIGINT_TAG]);
    }
    if (typeof value[BYTES_TAG] === "string") {
      const hex = value[BYTES_TAG];
      const out = new Uint8Array(hex.length / 2);
      for (let i = 0; i < out.length; i += 1) {
        out[i] = parseInt(hex.substr(i * 2, 2), 16);
      }
      return out;
    }
  }
  return value;
}

export function stringify(value) {
  return JSON.stringify(value, replacer);
}

/**
 * Never throws. A corrupt or truncated payload returns `fallback` so a bad
 * journal can never break app boot — the caller treats that as "start clean".
 */
export function parse(text, fallback = null) {
  if (typeof text !== "string" || text.length === 0) return fallback;
  try {
    const out = JSON.parse(text, reviver);
    return out === undefined ? fallback : out;
  } catch (err) {
    return fallback;
  }
}

/**
 * Deep-clone through the codec. Used when handing journal rows to React so a
 * consumer mutating a value can never corrupt the in-memory cache.
 */
export function clone(value) {
  return parse(stringify(value));
}

/**
 * Errors are not JSON-serialisable in any useful way (`JSON.stringify(new Error("x"))`
 * is `{}`). Reduce to the fields we actually surface, and keep it small — this
 * is stored in `localStorage` under a 5 MB budget.
 */
/** The custom-error name viem decoded for a revert (e.g. "RouterPaused"), if any. */
function revertErrorName(err) {
  let e = err;
  for (let depth = 0; e && depth < 6; depth++, e = e.cause) {
    if (e.errorName) return String(e.errorName);
    if (e.data?.errorName) return String(e.data.errorName);
  }
  return null;
}

export function serializeError(err) {
  if (!err) return null;
  if (typeof err === "string") return { name: "Error", message: err };
  return {
    name: err.name || "Error",
    // viem errors carry a long `message` plus a `shortMessage`; prefer the short
    // one for display and cap the long one so a stack-heavy revert reason cannot
    // blow the storage quota.
    message: String(err.shortMessage || err.message || err).slice(0, 500),
    code: err.code ?? err.cause?.code ?? null,
    // The revert's name survives the round trip, so a screen can still tell
    // e.g. a paused router from any other failure (midl-server plan §13.9 F1).
    errorName: revertErrorName(err),
  };
}

export const __testing = { BIGINT_TAG, BYTES_TAG, replacer, reviver };
